import { useEffect, useRef, useState } from "react";
import { IoAdd } from "react-icons/io5";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import TagInput from "src/Pages/WO/Components/forms/components/TagInput";
import {
  ChevronToggle,
  Dropzone,
  FieldLabel,
  GhostButton,
  ModalShell,
  PrimaryButton,
  ServiceTicketToggle,
  TrashIcon,
  compressIfNeeded,
  formatBytes,
} from "./uploadModalUI";
import { noteFilesUploaded } from "src/Pages/KnowledgeBase/kbSession";

// Jobsite photos/videos are WoAttachment rows with type === 1. The /attach
// upload flow uses type 1 ("attachments to the work order"); the per-file
// metadata.attachmentType then narrows it to a jobsite photo.
const JOBSITE_PHOTO_TYPE = 1;
const ATTACHMENT_UPLOAD_FLOW = 1;

// HEIC/HEIF are covered by `image/*`, but many browsers report an empty
// `file.type` for them, so the explicit extensions are listed too (the dropzone
// falls back to extension matching when the MIME is missing).
const MEDIA_ACCEPT = "image/*,video/*,.heic,.heif";
const MEDIA_FOOTER = "Supported formats: JPG, PNG, HEIC, MP4, MOV — videos up to 100 MB";
// Jobsite videos are capped at 100 MB.
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

const isVideoFile = (file) =>
  file?.type?.startsWith("video/") || /\.(mp4|mov|webm|avi|mkv|m4v|3gp)$/i.test(file?.name || "");

let itemIdCounter = 0;
const nextItemId = () => `media-${Date.now()}-${itemIdCounter++}`;

const buildItem = (file) => {
  const video = isVideoFile(file);
  return {
    id: nextItemId(),
    file,
    isVideo: video,
    previewUrl: URL.createObjectURL(file),
    documentName: file.name,
    tags: [],
    description: "",
    // Videos can't be included in the service ticket; photos default on.
    includeInServiceTicket: !video,
  };
};

/**
 * "Upload Photo/Video" modal. Starts on an empty dropzone; once files are chosen
 * it shows an expandable card per file (Name / Tag / Description / Include In
 * Service Ticket) with a thumbnail preview, plus "Add More" and "Upload All (N)".
 *
 * Videos are capped at 100 MB and can't be included in the service ticket.
 */
const UploadPhotosModal = ({ shown, order, onClose, onUploaded }) => {
  const currentUser = UserService.getCurrentUser();
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Mirror of `items` so the unmount cleanup can revoke the latest preview URLs.
  const itemsRef = useRef([]);
  itemsRef.current = items;

  // Reset whenever the modal (re)opens.
  useEffect(() => {
    if (!shown) return;
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setItems([]);
    setExpandedId(null);
    setUploading(false);
  }, [shown]);

  // Revoke any outstanding preview URLs on unmount to avoid leaks.
  useEffect(() => () => itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl)), []);

  if (!shown) return null;

  const addFiles = (files) => {
    // Reject oversized videos before building preview entries.
    const tooLarge = files.filter((file) => isVideoFile(file) && file.size > MAX_VIDEO_BYTES);
    if (tooLarge.length) {
      toast(`Video exceeds the 100 MB limit: ${tooLarge.map((f) => f.name).join(", ")}`);
    }
    const accepted = files.filter((file) => !(isVideoFile(file) && file.size > MAX_VIDEO_BYTES));
    if (!accepted.length) return;
    const built = accepted.map(buildItem);
    setItems((prev) => {
      const next = [...prev, ...built];
      if (!prev.length && built[0]) setExpandedId(built[0].id);
      return next;
    });
  };

  const updateItem = (id, patch) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const removeItem = (id) =>
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });

  const handleUpload = async () => {
    if (!items.length || !order?.id) return;
    setUploading(true);
    try {
      const files = [];
      const metadata = [];
      for (const item of items) {
        files.push(await compressIfNeeded(item.file, imageCompression));
        metadata.push({
          attachmentType: JOBSITE_PHOTO_TYPE,
          documentName: item.documentName?.trim() || item.file.name,
          tags: item.tags,
          description: item.description?.trim() || "",
          // Videos are never included in the service ticket.
          includeInServiceTicket: item.isVideo ? false : item.includeInServiceTicket,
        });
      }
      const res = await WOService.uploadFiles(files, order.id, ATTACHMENT_UPLOAD_FLOW, currentUser, metadata);
      if (res.status === 201) {
        toast(`${files.length} file${files.length > 1 ? "s" : ""} uploaded successfully`);
        onUploaded?.();
        noteFilesUploaded();
        onClose?.();
      } else {
        toast("Something went wrong");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  // ---- Empty state: dropzone ----
  if (!items.length) {
    return (
      <ModalShell
        title="Upload Photo/Video"
        width="w-[500px]"
        onClose={onClose}
        footer={<GhostButton onClick={onClose}>Cancel</GhostButton>}
      >
        <Dropzone
          accept={MEDIA_ACCEPT}
          multiple
          title="Upload Photo/Video"
          hint="Drag & Drop files here"
          footer={MEDIA_FOOTER}
          onFiles={addFiles}
          onReject={(message) => toast(message)}
        />
      </ModalShell>
    );
  }

  // ---- File list with per-file metadata ----
  return (
    <ModalShell
      title="Upload Photos/Videos"
      badge={`${items.length} File${items.length > 1 ? "s" : ""}`}
      onClose={onClose}
      footer={
        <>
          <label className="h-[40px] px-1 flex items-center gap-2 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#0065FD] cursor-pointer mr-auto">
            <IoAdd size={18} />
            Add More
            <input
              type="file"
              accept={MEDIA_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) addFiles(files);
                if (e.target) e.target.value = null;
              }}
            />
          </label>
          <GhostButton onClick={onClose} disabled={uploading}>
            Cancel
          </GhostButton>
          <PrimaryButton tour="upload-all" onClick={handleUpload} loading={uploading}>
            Upload All ({items.length})
          </PrimaryButton>
        </>
      }
    >
      <div className="p-6 flex flex-col gap-4">
        {items.map((item) => {
          const expanded = item.id === expandedId;
          return (
            <div
              key={item.id}
              className={`rounded-[12px] border bg-white ${
                expanded ? "border-[#C9D7F2]" : "border-[#E5E8F3]"
              }`}
            >
              {/* Card header — keeps its background whether or not the card is
                  expanded. The card itself isn't clipped (no overflow-hidden) so
                  the Tag dropdown can overflow the card; the header rounds its own
                  corners instead. */}
              <div className={`flex items-center gap-3 px-4 py-3 bg-[#F8F9FE] ${expanded ? "rounded-t-[12px]" : "rounded-[12px]"}`}>
                <div className="relative w-[40px] h-[40px] shrink-0 rounded-[6.67px] overflow-hidden bg-[#DBEAFE]">
                  {item.isVideo ? (
                    <>
                      <video src={item.previewUrl} className="w-full h-full object-cover" muted preload="metadata" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
                          <path d="M0 1L12 7L0 13V1Z" fill="#FFFFFF" />
                        </svg>
                      </span>
                    </>
                  ) : (
                    <img src={item.previewUrl} alt={item.documentName} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate" title={item.file.name}>
                    {item.file.name}
                  </span>
                  <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                    {formatBytes(item.file.size)}
                    {item.isVideo ? " • Video" : ""}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="p-2 text-[#7C829A] cursor-pointer hover:bg-slate-100 rounded-[6px]"
                  aria-label="Remove file"
                >
                  <TrashIcon />
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : item.id)}
                  className="p-1 cursor-pointer"
                  aria-label={expanded ? "Collapse" : "Expand"}
                >
                  <ChevronToggle expanded={expanded} />
                </button>
              </div>

              {expanded && (
                <div className="px-4 pb-4 flex flex-col gap-4 border-t border-[#E5E8F3] pt-4">
                  <div className="flex flex-col gap-1">
                    <FieldLabel>Name</FieldLabel>
                    <input
                      value={item.documentName}
                      onChange={(e) => updateItem(item.id, { documentName: e.target.value })}
                      className="h-[40px] px-3 border border-[#E5E8F3] rounded-[6px] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Tag</FieldLabel>
                    <TagInput selectedTags={item.tags} onChange={(tags) => updateItem(item.id, { tags })} />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Description</FieldLabel>
                    <textarea
                      value={item.description}
                      onChange={(e) => updateItem(item.id, { description: e.target.value })}
                      placeholder="Add notes..."
                      rows={2}
                      className="p-3 bg-[#FBFCFF] border border-[#E5E8F3] rounded-[6px] outline-none resize-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
                    />
                  </div>

                  <ServiceTicketToggle
                    checked={!item.isVideo && item.includeInServiceTicket}
                    disabled={item.isVideo}
                    onChange={() =>
                      !item.isVideo && updateItem(item.id, { includeInServiceTicket: !item.includeInServiceTicket })
                    }
                    label="Include In Service Ticket"
                    helper={item.isVideo ? "Videos can't be included in the service ticket." : undefined}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
};

export default UploadPhotosModal;
